import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { Loader2, Paperclip, Upload } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Chip } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";
import { dateTime } from "@/lib/format";

const KIND_LABEL = { document: "Document", image: "Image", communication: "Communication" };

const fileSize = (bytes = 0) => {
  if (!bytes) return "0 bytes";
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(bytes < 10240 ? 1 : 0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Attachments on an evidence item: documents, images and saved communications. */
export const EvidenceAttachments = ({ evidence, canWrite, onChange }) => {
  const [rows, setRows] = useState(evidence.attachments || []);
  const [kind, setKind] = useState("document");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const res = await api.get("/regulatory/evidence");
    const found = res.data.find((e) => e.id === evidence.id);
    setRows(found?.attachments || []);
    if (onChange) await onChange();
  }, [evidence.id, onChange]);

  useEffect(() => { setRows(evidence.attachments || []); }, [evidence]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const body = new FormData();
    body.append("file", file);
    body.append("kind", kind);
    body.append("note", note);
    try {
      await api.post(`/regulatory/evidence/${evidence.id}/attachments`, body,
                     { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${file.name} attached and hashed`);
      setNote("");
      await refresh();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const open = (attachment) => {
    const base = BACKEND_URL;
    window.open(`${base}/api/regulatory/evidence/${evidence.id}/attachments/`
      + `${attachment.attachment_id}?token=${localStorage.getItem("servecor_token") || ""}`, "_blank");
  };

  return (
    <div className="space-y-3" data-testid="evidence-attachments">
      {rows.length === 0 && (
        <p className="text-[11.5px] text-muted-foreground">Nothing attached to this evidence yet.</p>
      )}
      {rows.map((a) => (
        <div key={a.attachment_id} data-testid={`evidence-attachment-${a.attachment_id}`}
             className="flex items-start justify-between gap-3 rounded-xl border border-border bg-muted/20 p-3">
          <div className="min-w-0">
            <button type="button" onClick={() => open(a)}
                    data-testid={`evidence-attachment-open-${a.attachment_id}`}
                    className="flex items-center gap-1.5 text-[12.5px] text-foreground hover:text-primary">
              <Paperclip className="h-3.5 w-3.5 text-primary" />
              <span className="truncate">{a.filename}</span>
            </button>
            <div className="mt-1 font-mono text-[10px] text-muted-foreground">
              sha256 {a.sha256?.slice(0, 16)}… · {fileSize(a.size)} ·{" "}
              {a.uploaded_by} · {dateTime(a.uploaded_at)}
            </div>
            {a.note && <div className="mt-1 text-[11px] text-muted-foreground">{a.note}</div>}
          </div>
          <Chip className="border-primary/40 bg-primary/10 text-primary">
            {KIND_LABEL[a.kind] || a.kind}
          </Chip>
        </div>
      ))}

      {canWrite && (
        <div className="space-y-2 rounded-xl border border-border p-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Attachment type</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="evidence-attachment-kind" className="bg-muted/40 text-[12px]">
                  <SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="image">Image or photograph</SelectItem>
                  <SelectItem value="communication">Communication (email, message)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[11px] text-muted-foreground">Note (optional)</Label>
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                     data-testid="evidence-attachment-note"
                     placeholder="What this shows" className="bg-muted/40 text-[12px]" />
            </div>
          </div>
          <label className="flex cursor-pointer items-center gap-2 text-[12px] text-primary">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {busy ? "Uploading…" : "Choose a file to attach"}
            <input type="file" className="hidden" data-testid="evidence-attachment-input"
                   accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.eml,.msg,.rtf,.html,.json,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                   onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          <p className="text-[10.5px] text-muted-foreground">
            Documents, images and saved communications — pdf, docx, xlsx, csv, txt, eml, msg, png,
            jpg — up to 20 MB. Each file is SHA-256 hashed on upload, so tampering is detectable.
          </p>
        </div>
      )}
    </div>
  );
};
